import { z } from 'zod';
import { BuildingIdSchema } from '../ids';
import { ItemAmountsSchema, ItemIdSchema } from './items';

/**
 * The id of a structure definition (CORE-ENTITY): data in the game config's `structures`, never
 * a closed list, so a new structure needs no code. The format only; the server checks the
 * definition exists. Ids are a public contract (database, wire, i18n `buildings.<id>`).
 */
export const StructureDefIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(32);
export type StructureDefId = z.infer<typeof StructureDefIdSchema>;

/**
 * The id of a module of a structure (BUILD, plan E3): data in its definition. The format only.
 * Ids are a public contract (database, wire, i18n `modules.<id>`).
 */
export const StructureModuleIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(32);
export type StructureModuleId = z.infer<typeof StructureModuleIdSchema>;

/** A rectangle of tiles a structure occupies, from its top-left tile. */
export const FootprintSchema = z.object({
  width: z.number().int().positive().max(32),
  height: z.number().int().positive().max(32),
});
export type Footprint = z.infer<typeof FootprintSchema>;

/**
 * A building as every visitor of a town sees it. Position is its top-left footprint tile; its
 * footprint is fixed when it is built (a later config change never resizes what stands).
 */
/**
 * How bodies meet a structure (plan E11): absent, it is solid and its entrance must stay
 * reachable; `none`, solid without an entrance (a palisade's segment); `through`, walked through
 * (a gate). Fixed from its definition when it was placed.
 */
/**
 * How bodies meet a piece: `none` solid (a segment), `through` walked through (a gate), `trench`
 * walked through slowly, its footprint a trench (BX-2: the ditch).
 */
export const StructureAccessSchema = z.enum(['none', 'through', 'trench']);
export type StructureAccess = z.infer<typeof StructureAccessSchema>;

export const BuildingSchema = z.object({
  id: BuildingIdSchema,
  kind: StructureDefIdSchema,
  /** The modules installed on it, by id, in the order they were (BUILD, plan E3). */
  modules: z.array(StructureModuleIdSchema),
  tx: z.number().int().nonnegative(),
  ty: z.number().int().nonnegative(),
  width: FootprintSchema.shape.width,
  height: FootprintSchema.shape.height,
  /** Where its entrance faces, in quarter turns clockwise from south (ADR 0061); absent: 0. */
  rotation: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]).optional(),
  access: StructureAccessSchema.optional(),
  /** The line it was raised in (a palisade's segment or gate, plan E11): the project's id. */
  line: BuildingIdSchema.optional(),
  /** Its lock was forced (plan C6): it stands open to anyone until its owner locks it again. */
  forced: z.literal(true).optional(),
  /** Its integrity now, when broken short of whole (SIEGE H2a); absent, whole (`integrityMax`). */
  integrity: z.number().int().nonnegative().optional(),
  /**
   * Fallen to ruin (SI-4): it stands but does nothing; a storage open to anyone, a line's segment
   * walked over. Its owner repairs it.
   */
  ruined: z.literal(true).optional(),
  /** The times it was patched (R-59): each takes from its whole integrity. */
  patches: z.number().int().positive().optional(),
  /**
   * When it last sprang, a trap (SIEGE H2e; game time): armed again its `trap.rearmMs` after while
   * its `springs` are no more than its `trap.rearms` (TP-3). Absent: not since it was repaired.
   */
  sprungAt: z.number().int().nonnegative().optional(),
  /** The times it sprang since it was last repaired (TP-3); absent, none. */
  springs: z.number().int().positive().optional(),
  /**
   * Its condition when last settled and when (durability, DR-4..6; `conditionAt` in the
   * simulation with its lot's neglect): absent, whole.
   */
  condition: z.number().min(0).max(1).optional(),
  conditionAt: z.number().int().nonnegative().optional(),
  /** The shots left in a turret's magazine (SIEGE H2e): told only to its lot's people (OC-2). */
  ammo: z.number().int().nonnegative().optional(),
  /**
   * A gate with a lock, shut (BUILD, audit item 11): solid to everyone until its people open it,
   * a forcing or a ruin leaving it open.
   */
  closed: z.literal(true).optional(),
  /** Until when its people opened it (game time): the shard shuts it then, nobody in its way. */
  openUntil: z.number().int().nonnegative().optional(),
  /**
   * A fixed fire lit since then (game time, LM-6): it burns only in the dark of the day (LX-9),
   * lighting what is near while something conceals, until its fire is spent. Absent: out.
   */
  litSince: z.number().int().nonnegative().optional(),
  /** Its time of fire: what it had when lit, or what it keeps while out (LM-6). `fireAt` reads both. */
  fuelMs: z.number().int().nonnegative().optional(),
});
export type Building = z.infer<typeof BuildingSchema>;

/**
 * A segment of a line being raised (plan E11): the structure it becomes (a palisade's segment or
 * a gate), where it stands in tiles of its site, turned, and how bodies meet it.
 */
export const LineSegmentSchema = z.object({
  kind: StructureDefIdSchema,
  tx: z.number().int().nonnegative(),
  ty: z.number().int().nonnegative(),
  width: FootprintSchema.shape.width,
  height: FootprintSchema.shape.height,
  rotation: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
  access: StructureAccessSchema,
});
export type LineSegment = z.infer<typeof LineSegmentSchema>;

/**
 * A structure being raised (BUILD, plan E1): its footprint stands from the moment it is placed,
 * like scaffolding; materials are carried to it until its recipe is complete (and, from E2, its
 * work done), then the structure is born with the same id. Position as a building's.
 */
export const ProjectSchema = BuildingSchema.omit({ modules: true }).extend({
  /** The module it raises on `target` (plan E3); null when it raises a structure. */
  module: StructureModuleIdSchema.nullable(),
  /** The building a module project raises its module on; null for a structure. */
  target: BuildingIdSchema.nullable(),
  /** What it takes, fixed when it was placed. */
  recipe: ItemAmountsSchema,
  /** What has been carried to it so far (never more than the recipe). */
  delivered: ItemAmountsSchema,
  /** Units of work done and needed (BUILD technique 5; 0 of 0 until E2). */
  workDone: z.number().int().nonnegative(),
  workTotal: z.number().int().nonnegative(),
  /**
   * A line's segments (plan E11): its footprint is theirs, not its rectangle (their bounding
   * box), and each becomes a structure of its own when it is done.
   */
  segments: z.array(LineSegmentSchema).optional(),
});
export type Project = z.infer<typeof ProjectSchema>;

/**
 * A structure's components (CORE-ENTITY): the numbers of each one it has. A definition's base
 * has them; a module adds to them (capacities, rates and the station's tier add up).
 */
export const StructureComponentsSchema = z.object({
  /** Posts for NPC workers of a trade (PROD-WORKER, AP-2), and the share of their yield it takes. */
  posts: z
    .object({
      trade: z.string(),
      count: z.number().int().positive(),
      share: z.number().positive().optional(),
    })
    .optional(),
  /** Beds for NPC workers (PROD-WORKER technique 9): its site's workers sleep in them. */
  beds: z.object({ count: z.number().int().positive() }).optional(),
  /** A craft's workstation and its tier (CRAFT-STATION, plan E7; its recipes come with E9). */
  workstation: z.object({ craft: z.string(), tier: z.number().int().positive() }).optional(),
  /**
   * A storage: its capacity in load units. The `main` one is the town's, the only one that pays
   * and receives in the town's economy; the others only keep goods.
   */
  storage: z
    .object({ capacity: z.number().int().positive(), main: z.literal(true).optional() })
    .optional(),
  /**
   * A lock (plan C6, QT-4): forced by `stages`, each `forceMs` long; without one a storage stands
   * open.
   */
  lock: z
    .object({ forceMs: z.number().int().positive(), stages: z.number().int().positive() })
    .optional(),
  /** Where fragments are absorbed and extracted (MAGIC-STATION). */
  attunement: z.object({}).optional(),
  /** A shooter defending its lot (SIEGE H2e): its sheet, its shots' item and its magazine. */
  turret: z
    .object({ sheet: z.string(), ammo: z.string(), magazine: z.number().int().positive() })
    .optional(),
  /** A producer of `produces`: units per hour of game time, up to its own capacity. */
  production: z
    .object({ ratePerHour: z.number().positive(), capacity: z.number().int().positive() })
    .optional(),
});
export type StructureComponents = z.infer<typeof StructureComponentsSchema>;

/**
 * What raising a structure's base costs, the components it has, and its coins a game day to keep on
 * a lot (durability, DR-1 a)); absent, nothing.
 */
export const StructureBaseSchema = StructureComponentsSchema.extend({
  cost: ItemAmountsSchema,
  upkeep: z.number().nonnegative().optional(),
});
export type StructureBase = z.infer<typeof StructureBaseSchema>;

/**
 * A module a structure takes (BUILD technique 1, plan E3): a concrete piece (a furnace, a second
 * shaft) with its cost, what it adds to the components, the modules it needs first, and whether
 * it is raised as a project (else installed at once).
 */
export const StructureModuleSchema = z.object({
  cost: ItemAmountsSchema,
  /** Coins a game day it costs to keep on a lot (plan E14); absent, nothing. */
  upkeep: z.number().nonnegative().optional(),
  /** What it adds to its base's components (any of them may add nothing). */
  adds: z
    .object({
      posts: z.object({ count: z.number().int().nonnegative() }),
      beds: z.object({ count: z.number().int().nonnegative() }),
      storage: z.object({ capacity: z.number().int().nonnegative() }),
      workstation: z.object({ tier: z.number().int().nonnegative() }),
      /** Metres more a tower's lookout sees (LX-2). */
      watch: z.object({ radius: z.number().nonnegative() }),
      /** What its structure breaks as with it (LM-2): the hardest wins. */
      material: z.enum(['wood', 'stone', 'metal']),
      /** The deck it lays over its structure (ADR 0076: a walkway), rows at rotation 0. */
      deck: z.array(z.string()),
      lock: z.object({
        forceMs: z.number().int().nonnegative(),
        stages: z.number().int().nonnegative(),
      }),
      production: z.object({
        ratePerHour: z.number().nonnegative(),
        capacity: z.number().int().nonnegative(),
      }),
    })
    .partial(),
  requires: z.array(StructureModuleIdSchema).optional(),
  /** A piece of one of `kinds` within `reach` tiles of its structure on its lot (LM-2). */
  beside: z
    .object({ kinds: z.array(z.string()), reach: z.number().int().nonnegative() })
    .optional(),
  project: z
    .object({ stages: z.number().int().positive(), work: z.number().int().nonnegative() })
    .optional(),
});
export type StructureModule = z.infer<typeof StructureModuleSchema>;

/** A structure definition as the client needs it: the build palette, panels and previews. */
export const StructureDefSchema = z.object({
  footprint: FootprintSchema,
  /** Whether players may place it in their town. */
  buildable: z.boolean(),
  /** How many of it a town may have; absent = no limit. */
  perTown: z.number().int().positive().optional(),
  /** What its production component makes. */
  produces: z.string().optional(),
  /** Its base: what raising it costs, and its components. */
  base: StructureBaseSchema,
  /** The modules it takes (plan E3), by id, in the order players see them. */
  modules: z.record(StructureModuleIdSchema, StructureModuleSchema),
  /** Claimable: taking control of it costs this. */
  claim: z.object({ cost: ItemAmountsSchema }).optional(),
  /** Lootable by others: the share taken, the interaction's length and the protection after. */
  loot: z
    .object({
      fraction: z.number().positive(),
      durationMs: z.number().int().nonnegative(),
      protectionMs: z.number().int().nonnegative(),
    })
    .optional(),
  /** Repairable once damaged, for this cost. */
  repair: z.object({ cost: ItemAmountsSchema }).optional(),
  /** Raised as a project (BUILD): its base cost is carried to it instead of paid at once. */
  project: z
    .object({ stages: z.number().int().positive(), work: z.number().int().nonnegative() })
    .optional(),
  /** The hearth (HOME, TI-23): placed with its lot, never in a town. */
  hearth: z.object({}).optional(),
  /**
   * A trap (SIEGE H2e): a marker only, its numbers the server's. Clients never draw it as a piece
   * of a lot: what they see of traps is the server's `traps` (OC-2).
   */
  trap: z.looseObject({}).optional(),
  /** Traced as a line of segments (plan E11), up to `maxSegments`, with its `gate`. */
  line: z
    .object({
      maxSegments: z.number().int().positive(),
      gate: z.string().optional(),
      fits: z.array(z.string()).optional(),
    })
    .optional(),
  /** Walked through (a gate): it blocks no body and needs no way in. */
  passable: z.object({ surface: z.literal('trench').optional() }).optional(),
  /**
   * A temporary camp (plan C8): pitched from a kit, never built; it expires `lifeMs` after its
   * owner was last by it, and is wrecked in `wreckMs` in its region's Intense Pulse.
   */
  bivouac: z
    .object({ lifeMs: z.number().int().positive(), wreckMs: z.number().int().nonnegative() })
    .optional(),
  /** Where it may stand (C5): a town's plot, a lot, or both. */
  sites: z.array(z.enum(['lot'])),
  /** What a siege breaks it as (SIEGE SG-1); absent, wood. */
  material: z.enum(['wood', 'stone', 'metal']).optional(),
  /**
   * What a producer may be sown with (LM-5, LX-4): each crop's seeds (spent once, at sowing), the
   * good it makes and its rate's factor (absent: 1).
   */
  crops: z
    .record(
      z.string(),
      z.object({
        seeds: ItemAmountsSchema,
        produces: ItemIdSchema,
        rate: z.number().positive().optional(),
      }),
    )
    .optional(),
  /** What it lays above the ground (ADR 0076: a stair), rows of its footprint at rotation 0. */
  deck: z.array(z.string()).optional(),
  /** Its interior (WORLD-INTERIOR phase 2), rows of its footprint at rotation 0 in room symbols. */
  rooms: z.array(z.string()).optional(),
  /** A fixed fire (LM-6): how far it lights, what it burns (ms a unit), how much it holds. */
  burner: z
    .object({
      light: z.number().positive(),
      burns: z.record(z.string(), z.number().int().positive()),
      holdsMs: z.number().int().positive(),
    })
    .optional(),
  /** One of a camp's principal buildings (RX-5). */
  principal: z.literal(true).optional(),
});
export type StructureDef = z.infer<typeof StructureDefSchema>;

/** Every structure definition, in the order players see them (sent by the server). */
export const StructureCatalogSchema = z.record(StructureDefIdSchema, StructureDefSchema);
export type StructureCatalog = z.infer<typeof StructureCatalogSchema>;

/** Crafting (CRAFT-STATION, B064): the stations' recipes, their speeds, and what is made by hand. */
export const CraftingRulesSchema = z.object({
  /**
   * The stations' recipes (CRAFT-STATION, plan E9a), by id: the craft whose station runs them from
   * `level`, what a run takes, gives and burns (one of `fuel.items`), its time at speed 1.
   */
  stations: z.record(
    z.string(),
    z.object({
      craft: z.string(),
      level: z.number().int().positive(),
      inputs: ItemAmountsSchema,
      output: ItemAmountsSchema,
      fuel: z
        .object({ items: z.array(ItemIdSchema), count: z.number().int().positive() })
        .nullable(),
      timeMs: z.number().int().positive(),
    }),
  ),
  /** A station's speed by its level, from 1 (TA-2). */
  speeds: z.array(z.number().positive()),
  /** A public workshop (SR-2): the coins a run costs, and how near its door it is used. */
  workshop: z.object({ fee: z.number().int().nonnegative(), reach: z.number().positive() }),
  /** The mastery each craft practices (TA-1): its ranks make that craft cheaper. */
  masteries: z.record(z.string(), z.string()),
  /** What is made by hand (B064), by the item made: its goods and its time. */
  hand: z.record(
    ItemIdSchema,
    z.object({ inputs: ItemAmountsSchema, timeMs: z.number().int().positive() }),
  ),
  /**
   * Mending at a station (ITEM-WEAR §4, §9, DB-118): the rules the shared `repairTerms` reads, the
   * mastery's top rank, and by item what wears, the craft that mends it, its original maximum and
   * the tier its price reads; the server still judges.
   */
  repair: z.object({
    coinsPerPoint: z.number().positive(),
    tierFactors: z.array(z.number().positive()),
    loss: z.number().min(0),
    masterLoss: z.number().min(0),
    retireBelow: z.number().min(0),
    topRank: z.number().int().nonnegative(),
    items: z.record(
      ItemIdSchema,
      z.object({
        craft: z.string(),
        original: z.number().positive(),
        tier: z.number().int().positive(),
      }),
    ),
  }),
  /**
   * Salvaging at a station (ITEM-WEAR §4, WR-14, WR-15): by item, the craft whose station takes it
   * apart and what comes back; what is missing gives nothing back (`cannot_salvage`).
   */
  salvage: z.record(ItemIdSchema, z.object({ craft: z.string(), goods: ItemAmountsSchema })),
});
export type CraftingRules = z.infer<typeof CraftingRulesSchema>;
